// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Pointer
//
// Points to a measure:beat:subdivision within a piece of music, and handles
// simple math on this as well.
import type { TimeSignatureAlias } from 'shared/definitions/musicTheory/rhythms';
import { rhythmicFractions } from 'shared/definitions/musicTheory/rhythms';
import { findKey } from 'shared/src/utils/findKey';
import { isString } from 'shared/src/utils/isString';
import TimeSignature from './timeSignature';
import Rhythm from './rhythm';
import Fraction from './fraction';
import { isObject } from '../../../shared/src/utils/isObject';

type PointerRhythmFractionOrMBSString = string | Pointer | Rhythm | Fraction | MBSObject;

export type MBSObject = { measure: number; beat: number; subdivision: number };

export default class Pointer {
  /** Measure number */
  m!: number;

  /** Beat number */
  b!: number;

  /** Subdivision number */
  s!: number;

  /** The meter for this pointer, if it has one */
  meter: TimeSignature;

  /**
   * Create a new Pointer
   * @param {string} mbs in format "measureNumber:beatNumber:subdivisionNumber"
   * @param {TimeSignature|string} [timeSig]
   *
   * @property {number} m measure number
   * @property {number} b beat number
   * @property {number} s subdivision number
   */
  constructor(mbs: string | MBSObject, timeSig: TimeSignature | TimeSignatureAlias) {
    this.meter = TimeSignature.fromString(timeSig);
    this.set(mbs);
  }

  set(mbs?: string | MBSObject) {
    if (typeof mbs === 'undefined') mbs = '0:0:0';
    const mbsObject = mbsStringToMBSObject(mbs);
    this.m = mbsObject.measure;
    this.b = mbsObject.beat;
    this.s = mbsObject.subdivision;
  }

  setMeter(ts: TimeSignatureAlias | TimeSignature) {
    this.meter = TimeSignature.fromString(ts);
  }

  add(mbs: PointerRhythmFractionOrMBSString) {
    mbs = this.anythingToPointer(mbs);
    this.m += mbs.m;
    this.b += mbs.b;
    this.s += mbs.s;
    this.simplify();
    return this;
  }

  subtract(mbs: PointerRhythmFractionOrMBSString) {
    mbs = this.anythingToPointer(mbs);
    this.m -= mbs.m;
    this.b -= mbs.b;
    this.s -= mbs.s;
    this.simplify();
    return this;
  }

  equals(mbs: PointerRhythmFractionOrMBSString) {
    mbs = this.anythingToPointer(mbs);
    if (typeof this.meter !== 'undefined') {
      this.simplify();
      mbs.simplify();
    }

    return this.m === mbs.m && this.b === mbs.b && this.s === mbs.s;
  }

  isGreaterThan(mbs: PointerRhythmFractionOrMBSString) {
    mbs = this.anythingToPointer(mbs);
    if (typeof this.meter !== 'undefined') {
      this.simplify();
      mbs.simplify();
    }

    return Pointer.greaterThan(this, mbs);
  }

  isLessThan(mbs: PointerRhythmFractionOrMBSString) {
    mbs = this.anythingToPointer(mbs);
    if (typeof this.meter !== 'undefined') {
      this.simplify();
      mbs.simplify();
    }

    return Pointer.greaterThan(mbs, this);
  }

  isGreaterThanOrEquals(pointer: PointerRhythmFractionOrMBSString) {
    return this.isGreaterThan(pointer) || this.equals(pointer);
  }

  isLessThanOrEquals(pointer: PointerRhythmFractionOrMBSString) {
    return this.isLessThan(pointer) || this.equals(pointer);
  }

  isDownbeat() {
    // Simplify first!
    const p = this.clone().simplify();
    return p.b === 0 && p.s === 0;
  }

  /**
   * Get the next closest beat. If we are in the middle of a beat, return the
   * start of the next beat. If we are at the beginning of a beat, return the
   * next beat.
   */
  getNextBeat() {
    const p = this.clone();
    p.simplify();
    p.b++;
    p.s = 0;
    p.simplify();
    return p;
  }

  /**
   * Get the closest beat before this pointer. If we are in the middle of a
   * beat, return the start of the beat we're currently in. If we're at the very
   * beginning of a beat, return the prior beat.
   */
  getPreviousBeat() {
    const p = this.clone();
    p.simplify();
    if (p.s === 0) {
      p.b--;
    } else {
      p.s = 0;
    }
    p.simplify();
    return p;
  }

  /**
   * Get the pointer to the subdivision that follows this pointer. If we're at
   * the very beginning of a subdivision, that will still be the next
   * subdivision.
   */
  getNextSubdivision() {
    const p = this.clone();
    p.simplify();
    p.s = ~~p.s;
    p.s++;
    p.simplify();
    return p;
  }

  /**
   * Return the pointer of the subdivision the last subdivision to begin before
   * this pointer. If we're at the beginning of a subdivision, returns the one
   * before this. If we're mid subdivision, returns the beginning of the
   * subdivison that we are currently in.
   */
  getPreviousSubdivision() {
    const p = this.clone();
    p.simplify();
    p.s = Math.floor(p.s) === p.s ? p.s - 1 : Math.floor(p.s);
    p.simplify();
    return p;
  }

  /**
   * Get a pointer for the next barline.
   */
  getNextMeasure() {
    const p = this.clone();
    p.simplify();
    p.m++;
    p.s = 0;
    p.b = 0;
    p.simplify();
    return p;
  }

  /**
   * Get the prior barline. If we are at the beginning of a measure, return the
   * previous measure. If we are mid measure, return the beginning of the
   * measure we are currently in.
   */
  getPreviousMeasure() {
    const p = this.clone();
    p.simplify();
    if (p.b !== 0 || p.s !== 0) {
      p.b = 0;
      p.s = 0;
    } else {
      p.m--;
    }
    return p;
  }

  /**
   * Round this pointer to the nearest rhythmic subdivision.
   */
  quantizeTo(quantRhythm: Rhythm) {
    if (typeof this.meter === 'undefined') {
      throw new Error('Cannot get fraction without first defining a time signature.');
    }
    if (!(quantRhythm instanceof Rhythm)) {
      quantRhythm = new Rhythm(quantRhythm);
    }
    // Get leftover after dividing by the rhythm:
    // Divide thisFraction / quantRhythm = units
    // Round units up or down:
    const units = Math.round(this.toFraction().value() / quantRhythm.getFraction().value());

    // Now multiply the quant rhythm by units
    const fraction = quantRhythm.getFraction().multiply(units).simplify();
    // And set this from that fraction:
    const p = Pointer.anythingToPointer(fraction, this.meter);
    this.m = p.m;
    this.b = p.b;
    this.s = p.s;
    return this;
  }

  /**
   * Convert this (or another) pointer to a number of beats from 0.
   * @param {Pointer} mbs
   */
  toBeats(mbs: Pointer = this) {
    return (
      mbs.s / mbs.meter.subdivisionsPerBeat + // subdiv
      mbs.b + // beats
      mbs.m * mbs.meter.beatsPerMeasure
    ); // measures
  }

  toSeconds(tempo: number) {
    if (typeof tempo === 'undefined') {
      throw new Error('NaT: Tempo must be provided to convert a pointer to seconds.');
    }
    const beatSec = 60 / tempo;
    return this.toBeats() * beatSec;
  }

  /**
   * Return a fraction representing the total fractional measures in this pointer.
   */
  toFraction() {
    if (typeof this.meter === 'undefined') {
      throw new Error('Cannot get fraction without first defining a time signature.');
    }
    const fraction = this.meter.measureFraction
      .clone()
      .multiply(this.m)
      .add(this.meter.beatDuration.getFraction().multiply(this.b))
      .add(this.meter.subdivisionDuration.getFraction().multiply(this.s))
      .simplify();

    return fraction;
  }

  setFromBeats(b: number) {
    if (typeof this.meter === 'undefined') {
      throw new Error('Cannot set pointer from beats without first defining a time signature.');
    }
    this.m = 0;
    this.b = b;
    this.s = 0;
    this.simplify();
    return this;
  }

  anythingToPointer(mbs: PointerRhythmFractionOrMBSString) {
    return Pointer.anythingToPointer(mbs, this.meter);
  }

  /**
   * Reduce subdivisions, beats, measures after adding/subtracting as needed.
   */
  simplify() {
    if (typeof this.meter === 'undefined') return this;

    // First handle any negatives, aka "borrow" from larger columns:
    // So that only the measure column can be negative (like when we
    // say aloud: 3 measures before M, starting on the 3rd beat).
    while (this.s < 0) {
      this.b--;
      this.s += this.meter.subdivisionsPerBeat;
    }

    while (this.b < 0) {
      this.m--;
      this.b += this.meter.beatsPerMeasure;
    }

    // Now handle any fractions of a beat:
    this.s += (this.b % 1) * this.meter.subdivisionsPerBeat;
    this.b = ~~this.b;

    while (this.s >= this.meter.subdivisionsPerBeat) {
      this.b++;
      this.s -= this.meter.subdivisionsPerBeat;
    }

    while (this.b >= this.meter.beatsPerMeasure) {
      this.m++;
      this.b -= this.meter.beatsPerMeasure;
    }
    return this;
  }

  clone() {
    return new Pointer(this.toString(), this.meter);
  }

  toString() {
    return `${this.m}:${this.b}:${this.s}`;
  }

  /**
   * May return false if a note value doesn't exist for something (including tuplets)
   */
  toRhythm() {
    if (typeof this.meter === 'undefined') {
      throw new Error(
        'NaM: Cannot convert to rhythm without first defining a meter for the pointer.',
      );
    }

    const spb = this.meter.subdivisionsPerBeat;
    const bpm = this.meter.beatsPerMeasure;
    const subDuration = this.meter.subdivisionDuration;

    // Figure out how many subdivisions, of what note value:
    const s = this.s + this.b * spb + this.m * (bpm * spb);

    const fraction = subDuration.getFraction().multiply(s).simplify();
    let baseDurationFraction = fraction.clone();
    let dots = '';

    // If a note value doesn't exist for it, return false:
    if (
      fraction.numerator === 5 ||
      fraction.numerator >= 8 // anything else should have been simplified.
    )
      return false;

    // Single dots:
    if (fraction.numerator === 3) {
      baseDurationFraction = fraction.clone().multiply(2, 3).simplify();
      dots = '.';
    }

    // Double dots:
    if (fraction.numerator === 7) {
      baseDurationFraction = fraction.clone().multiply(4, 7).simplify();
      dots = '..';
    }

    // Triple dots: would be 15/x and multiply by 8/15.  But -- let's not.
    const baseDuration = findKey(rhythmicFractions, baseDurationFraction.toString());

    if (baseDuration === undefined) return false;
    return new Rhythm(baseDuration + dots);
  }

  static greaterThan(a: Pointer, b: Pointer) {
    return a.m > b.m || (a.m === b.m && a.b > b.b) || (a.m === b.m && a.b === b.b && a.s > b.s);
  }

  static fromSeconds({
    seconds,
    timeSignature,
    tempo,
  }: {
    seconds: number;
    timeSignature: TimeSignatureAlias | TimeSignature;
    tempo: number;
  }): Pointer {
    const secondsPerBeat = tempo / 60;
    const beatsElapsed = seconds / secondsPerBeat;
    const pointer = new Pointer(
      {
        measure: 0,
        beat: beatsElapsed,
        subdivision: 0,
      },
      timeSignature,
    );
    pointer.simplify();
    return pointer;
  }

  static anythingToPointer(
    mbs: PointerRhythmFractionOrMBSString,
    timeSig: TimeSignatureAlias | TimeSignature,
  ) {
    if (mbs instanceof Pointer) {
      if (typeof timeSig !== 'undefined') mbs.setMeter(timeSig);
      return mbs;
    }
    if (isMBSObject(mbs)) {
      mbs = new Pointer(mbs, timeSig);
      return mbs;
    }
    if (isString(mbs)) {
      mbs = new Pointer(mbs, timeSig);
      return mbs;
    }
    if (mbs instanceof Rhythm || mbs instanceof Fraction) {
      if (typeof timeSig === 'undefined') {
        throw new Error(
          'Undefined: You must add a time signatureto a pointer in order to perform math with rhythms or fractions.',
        );
      }
      timeSig = TimeSignature.fromString(timeSig);
      const toConvert = mbs instanceof Fraction ? mbs : mbs.getFraction();

      let measToAdd = 0;

      while (toConvert.greaterThan(timeSig.measureFraction)) {
        toConvert.subtract(timeSig.measureFraction);
        measToAdd++;
      }

      let beatsToAdd = 0;
      while (toConvert.greaterThan(timeSig.beatDuration.getFraction())) {
        toConvert.subtract(timeSig.beatDuration.getFraction());
        beatsToAdd++;
      }

      const subToAdd = toConvert.divide(timeSig.subdivisionDuration.getFraction()).value();

      const pointer = new Pointer(`${measToAdd}:${beatsToAdd}:${subToAdd}`, timeSig);

      pointer.simplify();
      return pointer;
    }
    throw new Error(
      `Not a Pointer: Value ${mbs} is not a pointer, rhythm, fraction or string -- a pointer cannot be created.`,
    );
  }

  /**
   * Return the last index number that is less than the given needle in a haystack
   * @param {Pointer} needle
   * @param haystack
   * @param {string} [haystackPropertyName]
   * @returns {number}
   */
  static afterIndex(needle: Pointer, haystack: any[], haystackPropertyName?: string) {
    const useProp = typeof haystackPropertyName !== 'undefined';
    let i;
    for (i = 0; i < haystack.length; i++) {
      const pointer = useProp ? haystack[i][haystackPropertyName] : haystack[i];
      if (needle.isLessThan(pointer)) return i - 1;
    }

    return i - 1;
  }

  /**
   * Filter an array to only include those object within the start/stop pointers provided.
   * @param start the earliest admissable pointer (or string mbs)
   * @param stop the latest admissable pointer (or string mbs)
   * @param haystack array of pointers, or objects with start and stop pointers on them
   * @param haystackStartName if defined, compare start times with haystack[i][haystackStartName]
   * @param haystackStopName if defined, compare stop times with haystack[i][haystackStopName]
   */
  static getArrayBetween<A>(
    start: Pointer,
    stop: Pointer,
    haystack: A[],
    haystackStartName?: string,
    haystackStopName?: string,
  ): A[] {
    const useStartProp = typeof haystackStartName !== 'undefined';
    const useStopProp = typeof haystackStopName !== 'undefined';

    return haystack.filter((el) => {
      const startPointer = (useStartProp
        ? el[haystackStartName as keyof typeof el]
        : el) as unknown as Pointer;
      const stopPointer = (useStopProp
        ? el[haystackStopName as keyof typeof el]
        : startPointer) as unknown as Pointer;

      if (
        startPointer.isGreaterThanOrEquals(start) &&
        ((useStopProp && stopPointer.isLessThanOrEquals(stop)) ||
          (!useStopProp && startPointer.isLessThan(stop)))
      ) {
        return true;
      }
      return false;
    });
  }
}

function mbsStringToMBSObject(mbs: string | MBSObject): MBSObject {
  if (isMBSObject(mbs)) return mbs;
  const parts = mbs.split(':');

  const numberParts: number[] = parts.map((part) => Number(part));
  return {
    measure: numberParts[0] || 0,
    beat: numberParts[1] || 0,
    subdivision: numberParts[2] || 0,
  };
}

function isMBSObject(mbs: any): mbs is MBSObject {
  if (
    isObject(mbs) &&
    'measure' in mbs &&
    typeof mbs.measure === 'number' &&
    'beat' in mbs &&
    typeof mbs.beat === 'number' &&
    'subdivision' in mbs &&
    typeof mbs.subdivision === 'number'
  )
    return true;
  return false;
}
